import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as L from 'leaflet';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';

import { GeoidService } from '../../../../global/components/forms/geoid-address/service/geoid.abstract.service';
import { Organisation } from '../../../../global/global.model';
import { MessageService } from '../../../../global/services/message/message.abstract.service';
import { organisationSelector } from '../../../../global/store/global.selectors';
import { Residence, ResidenceVerificationStatus } from '../../../../residence/residence.model';
import { ResidenceService } from '../../../../residence/service/residence.abstract.service';
import { State } from '../../../../store';
import { UserService } from '../../../../user/service/user.abstract.service';
import { Address } from '../../../../user/user.model';
import { I18nString, PermitCategory } from '../../../models/permit-category.model';
import { PermitTypeConfig } from '../../../models/permit-type.model';
import { LocationInfo } from '../../../models/permit.model';
import { createLocationMarkerIcon } from '../../../../global/utils/map/map-marker-icon.util';

/** One selectable address belonging to the requester, enriched with its residence status. */
interface RequesterAddressOption {
    id: string;
    displayName: string;
    formatted: string;
    address: Address;
    status: ResidenceVerificationStatus | null;
    isPrimary: boolean;
}

@Component({
    selector: 'app-permit-location-step',
    templateUrl: './permit-location-step.component.html',
    styleUrls: ['./permit-location-step.component.scss'],
})
export class PermitLocationStepComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() selectedCategory: PermitCategory | null = null;

    @Input() selectedType: PermitTypeConfig | null = null;

    /** The requester chosen in step 1 — their addresses are the candidates for the work location. */
    @Input() citizenId: string | null = null;

    @Input() initialLocation: LocationInfo | null = null;

    @Output() locationChanged = new EventEmitter<LocationInfo | null>();

    @Output() selectionStateChanged = new EventEmitter<boolean>();

    @Output() skipped = new EventEmitter<void>();

    searchControl = new FormControl();

    addressSuggestions: Observable<Address[]>;

    addressOptions: RequesterAddressOption[] = [];

    loadingAddresses = false;

    selectedOptionId: string | null = null;

    address = '';

    siteLocation = '';

    coordinates: { lat: number; lng: number } | null = null;

    mapError = false;

    private map: L.Map | null = null;

    private marker: L.Marker | null = null;

    private organisation: Organisation | null = null;

    private readonly defaultZoom = 16;

    private destroy$ = new Subject<void>();

    constructor(
        private geoidService: GeoidService,
        private userService: UserService,
        private residenceService: ResidenceService,
        private store: Store<State>,
        private translate: TranslateService,
        private messageService: MessageService,
    ) {
        this.addressSuggestions = this.searchControl.valueChanges.pipe(
            startWith(''),
            filter((value) => typeof value === 'string' && value.trim().length > 3),
            debounceTime(700),
            distinctUntilChanged(),
            switchMap((value: string) => this.geoidService.searchAddressQuery(value)),
        );
    }

    ngOnInit(): void {
        this.store
            .select(organisationSelector)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe((organisation: Organisation) => {
                this.organisation = organisation;
            });

        if (this.initialLocation) {
            this.address = this.initialLocation.address || '';
            this.siteLocation = this.initialLocation.siteLocation || '';
            this.coordinates = this.initialLocation.coordinates || null;
        }

        this.loadRequesterAddresses();

        // Deferred: the host binds this to the footer's enabled state, which has already been
        // checked by the time this component initialises.
        setTimeout(() => this.selectionStateChanged.emit(this.hasSelection));
    }

    ngAfterViewInit(): void {
        if (this.coordinates) {
            setTimeout(() => this.initializeMap(), 50);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.destroyMap();
    }

    localizedName(name: I18nString | string): string {
        if (!name) return '';
        if (typeof name === 'string') return name;
        const lang = this.translate.currentLang || 'fr';
        return name[lang] || name.fr || name.en || '';
    }

    // ─── Requester addresses ─────────────────────────────────────────────────

    /**
     * A permit applies to a property the applicant is attached to, so the candidates are the
     * requester's own addresses. Residences carry the name and verification state; the profile
     * address list is the read cache and also holds casual addresses with no residence claim.
     */
    private loadRequesterAddresses(): void {
        if (!this.citizenId) return;

        this.loadingAddresses = true;
        forkJoin([
            this.userService.getProfileById(this.citizenId).pipe(catchError(() => of(null))),
            this.residenceService.getCitizenResidences(this.citizenId).pipe(catchError(() => of([] as Residence[]))),
        ])
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(([profile, residences]) => {
                this.addressOptions = this.mergeAddresses(profile?.addresses || [], residences || []);
                this.loadingAddresses = false;
                this.preselectRestoredAddress();
            });
    }

    private mergeAddresses(addresses: Address[], residences: Residence[]): RequesterAddressOption[] {
        const matched = new Set<string>();

        const options: RequesterAddressOption[] = addresses.map((address, index) => {
            const residence = residences.find((r) => !matched.has(r._id) && this.sameAddress(r.address, address));
            if (residence) matched.add(residence._id);

            return {
                id: residence?._id || `address-${index}`,
                displayName: residence?.name || (address as any).name || this.formatAddress(address),
                formatted: this.formatAddress(address),
                address,
                status: residence?.verification?.status ?? null,
                isPrimary: residence?.isPrimary || (address as any).isPrimary === true,
            };
        });

        // Defensive: a residence with no matching entry in the cached profile list still belongs
        // to the requester and must be offerable.
        residences
            .filter((residence) => !matched.has(residence._id))
            .forEach((residence) => {
                options.push({
                    id: residence._id,
                    displayName: residence.name || this.formatAddress(residence.address),
                    formatted: this.formatAddress(residence.address),
                    address: residence.address,
                    status: residence.verification?.status ?? null,
                    isPrimary: residence.isPrimary,
                });
            });

        return options.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));
    }

    private sameAddress(a: Address, b: Address): boolean {
        return (
            this.normalize(a.streetName || a.street) === this.normalize(b.streetName || b.street) &&
            String(a.number || '') === String(b.number || '') &&
            this.normalize(a.postCode) === this.normalize(b.postCode) &&
            this.normalize(a.unit || '') === this.normalize(b.unit || '')
        );
    }

    private normalize(value: string): string {
        return (value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, '')
            .toLowerCase();
    }

    /** Re-highlight the card the agent had already picked when stepping back into this step. */
    private preselectRestoredAddress(): void {
        if (!this.address || this.selectedOptionId) return;
        const restored = this.addressOptions.find((option) => option.formatted === this.address);
        if (restored) this.selectedOptionId = restored.id;
    }

    selectAddressOption(option: RequesterAddressOption): void {
        this.selectedOptionId = option.id;
        this.address = option.formatted;
        this.searchControl.setValue('', { emitEvent: false });

        const { latitude, longitude } = option.address;
        this.coordinates = latitude != null && longitude != null ? { lat: latitude, lng: longitude } : null;

        this.refreshMap();
        this.emitChange();
    }

    isOptionSelected(option: RequesterAddressOption): boolean {
        return this.selectedOptionId === option.id;
    }

    statusBadgeKey(option: RequesterAddressOption): string {
        switch (option.status) {
            case 'APPROVED':
                return 'permit.create.location.badge_verified';
            case 'PENDING':
                return 'permit.create.location.badge_pending';
            case 'REJECTED':
                return 'permit.create.location.badge_rejected';
            default:
                return '';
        }
    }

    statusBadgeClass(option: RequesterAddressOption): string {
        switch (option.status) {
            case 'APPROVED':
                return 'badge-verified';
            case 'PENDING':
                return 'badge-pending';
            case 'REJECTED':
                return 'badge-rejected';
            default:
                return '';
        }
    }

    // ─── Manual address fallback ─────────────────────────────────────────────

    displayAddressFn = (address: Address | string | null): string => {
        if (!address) return '';
        return typeof address === 'string' ? address : this.formatAddress(address);
    };

    formatAddress(address: Address | null | undefined): string {
        if (!address) return '';
        return [address.unit ? `${address.unit}-` : '', address.number, address.direction, address.streetName || address.street, address.type, address.city, address.province, address.postCode]
            .map((part) => (typeof part === 'string' ? part.trim() : part))
            .filter((part) => !!part)
            .join(' ')
            .replace(/ ,/g, ',')
            .replace(/ {2,}/g, ' ')
            .replace(/^ | $/g, '')
            .replace(/,(?=\S)/g, ', ');
    }

    onAddressSelected(address: Address): void {
        if (!address) return;

        // Same guard the request wizard applies: an address outside the org is almost always a
        // mis-pick, and the permit would route to a city that can't act on it.
        const orgCity = this.organisation?.address?.city;
        if (orgCity && address.city && orgCity.toLowerCase() !== address.city.toLowerCase()) {
            this.messageService.error(this.translate.instant('permit.create.location.wrong_city', { addressCity: address.city, orgCity }));
            this.searchControl.setValue('', { emitEvent: false });
            return;
        }

        this.selectedOptionId = null;
        this.address = this.formatAddress(address);
        this.coordinates = address.latitude != null && address.longitude != null ? { lat: address.latitude, lng: address.longitude } : null;
        this.refreshMap();
        this.emitChange();
    }

    clearAddress(): void {
        this.selectedOptionId = null;
        this.address = '';
        this.coordinates = null;
        this.searchControl.setValue('', { emitEvent: false });
        this.destroyMap();
        this.emitChange();
    }

    // ─── Shared state ────────────────────────────────────────────────────────

    get hasSelection(): boolean {
        return !!this.address;
    }

    /** The dialog reads the final value from here when leaving the step. */
    getLocation(): LocationInfo | null {
        if (!this.hasSelection) return null;
        const location: LocationInfo = {};
        if (this.address) location.address = this.address;
        if (this.coordinates) location.coordinates = this.coordinates;
        if (this.siteLocation.trim()) location.siteLocation = this.siteLocation.trim();
        return location;
    }

    onSiteLocationChanged(): void {
        this.emitChange();
    }

    skip(): void {
        this.selectedOptionId = null;
        this.address = '';
        this.siteLocation = '';
        this.coordinates = null;
        this.skipped.emit();
    }

    private emitChange(): void {
        this.locationChanged.emit(this.getLocation());
        this.selectionStateChanged.emit(this.hasSelection);
    }

    // ─── Map (read-only preview of the chosen address) ───────────────────────

    private refreshMap(): void {
        if (!this.coordinates) {
            this.destroyMap();
            return;
        }
        if (!this.map) {
            setTimeout(() => this.initializeMap(), 0);
            return;
        }
        this.updateMarker(this.coordinates.lat, this.coordinates.lng);
    }

    private initializeMap(): void {
        if (this.map || !this.coordinates) return;

        const container = document.getElementById('permit-create-map');
        if (!container) {
            this.mapError = true;
            return;
        }

        try {
            this.map = L.map('permit-create-map', { zoomControl: false, scrollWheelZoom: false, dragging: false, doubleClickZoom: false }).setView(
                [this.coordinates.lat, this.coordinates.lng],
                this.defaultZoom,
            );
            L.tileLayer(MAP_TILE_URL, {
                maxZoom: 19,
                minZoom: 10,
                attribution: MAP_TILE_ATTRIBUTION,
            }).addTo(this.map);

            this.updateMarker(this.coordinates.lat, this.coordinates.lng);
            this.map.invalidateSize();
            this.mapError = false;
        } catch (_) {
            this.mapError = true;
        }
    }

    private updateMarker(lat: number, lng: number): void {
        if (!this.map) return;
        const latLng = L.latLng(lat, lng);
        if (this.marker) {
            this.marker.setLatLng(latLng);
        } else {
            this.marker = L.marker(latLng, { icon: createLocationMarkerIcon() }).addTo(this.map);
        }
        this.map.setView(latLng, this.defaultZoom);
        this.map.invalidateSize();
    }

    private destroyMap(): void {
        if (!this.map) return;
        try {
            this.map.remove();
        } catch (_) {
            // Leaflet throws if the container is already gone; nothing to clean up then.
        }
        this.map = null;
        this.marker = null;
    }
}
